import { DatabaseService } from "@/lib/databaseService"
import { Database } from "lucide-react"
import { create } from "zustand"
import { Transaction, Store, StoreGateway, Expense, MonthlyClosure, Gateway, User } from "@/types/entites"
import { isAfter, isBefore } from "date-fns"
// ==== Types ====
// export interface Store {
//   id: string
//   name: string
//   logoUrl?: string
//   createdAt: string
// }

// export interface Transaction {
//   id: string
//   description: string
//   gateway: string
//   amountGross: number
//   netAmount: number
//   depositDate: string
//   createdAt: string
//   deposited: boolean
//   depositedAt?: string
//   store_id: string
// }

// export interface Expense {
//   id: string
//   description: string
//   category: string
//   amount: number
//   paymentDate: string
//   createdAt: string
//   paid: boolean
//   paidAt?: string
//   paymentMethod?: string
//   store_id: string
// }

// export interface MonthlyClosure {
//   month: string
//   closedAt: string
//   note?: string
//   totals: {
//     salesDeposited: number
//     expensesPaid: number
//     netProfit: number
//     countSales: number
//     countExpenses: number
//     pendingSales: number
//     pendingExpenses: number
//   }
//   store_id: string
// }

function inRange(d: Date, start: Date, end: Date) {
  return !isBefore(d, start) && !isAfter(d, end)
}

// ==== Zustand Store ====
interface UserStoreState {
  // Auth/session
  token: string | null
  userId: string | null
  username: string | null
  role: string | null

  // Related user data
  stores: Store[]
  transactions: Transaction[]
  expenses: Expense[]
  monthlyClosures: MonthlyClosure[],
  gateways: Gateway[],
  users: Array<{ username: string; id: string; role: string; storeIds: string[] }>,


  /* Related accounting meta data */
  activeStoreId: string | null

  /* Login */
  login: (username: string, password: string) => Promise<{ ok: boolean; reason?: string }>
  logout: () => Promise<void>

  /* Get current user */
  getCurrentUser: () => { userId: string | null; username: string | null; role: string | null },
  addUser: (username: string, password: string, role: string, storeIds: string[]) => Promise<void>,
  updateUser: (id: string, patch: Partial<{ username: string; role: string; storeIds: string[] }>) => Promise<void>,
  changePassword: (id: string, newPassword: string) => Promise<void>,
  removeUser: (id: string) => Promise<void>,
  fetchUsers: () => Promise<void>

  // === Getters ===
  getStoreById: (id: string) => Store | undefined
  getTransactionsByStore: (storeId: string) => Transaction[]
  getExpensesByStore: (storeId: string) => Expense[]
  getClosuresByStore: (storeId: string) => MonthlyClosure[]

  // === Mutators ===
  setAuth: (token: string, userId: string, username: string, role: string) => void
  clearAuth: () => void

  /* Rehydrate user state */
  rehydrate: (id: string) => Promise<void>

  /* Store management */
  setStores: (stores: Store[]) => void
  setActiveStore:  (id: string) => Promise<void>
  addStore: (name: string, logoUrl?: string) => Promise<string>
  updateStore: (id: string, name: string, logoUrl?: string) => Promise<void>
  fetchStores: () => Promise<void>
  removeStore: (id: string) => Promise<void>
  getStoreBalance: () => number

  /* Transaction management */
  setTransactions: (txs: Transaction[]) => void
  addTransaction: (tx: Transaction) =>  Promise<void>
  fetchTransactions: () => Promise<void>
  updateTransaction: (id: string, patch: Partial<Transaction>) => Promise<void>
  removeTransaction: (id: string) => Promise<void>

  /* Expense management */
  setExpenses: (exps: Expense[]) => void
  fetchExpenses: () => Promise<void>
  addExpense: (exp: Expense) => Promise<void>
  updateExpense: (id: string, patch: Partial<Expense>) => Promise<void>
  removeExpense: (id: string) => Promise<void>

  /* Gateways management */
  fetchGatewaysByStore: () => Promise<Gateway[]>,
  addGateway: (name: string) => Promise<void>,
  removeGateway: (id: string) => Promise<void>,

  /* Monthly closures management */
  setMonthlyClosures: (closures: MonthlyClosure[]) => void
  computeMonthlySummary: (month: string) => MonthlyClosure['totals']
  addClosure: (closure: MonthlyClosure) => void
  removeClosure: (month: string, storeId: string) => void
}

export const useUserStore = create<UserStoreState>((set, get) => ({
  token: null,
  userId: null,
  username: null,
  role: null,

  stores: [],
  transactions: [],
  expenses: [],
  monthlyClosures: [],
  activeStoreId: null,
  gateways: [],
  users: [],


  rehydrate: async (id) => {
      const user = await DatabaseService.getUserById(id);
      set({ userId: user.id, username: user.username, role: user.role })

      if(user.role == 'admin') {
          const allStores = await DatabaseService.getAllStores();
          set({ stores: allStores })
      } else {
          /* Fetch stores by user Id */
          await get().fetchStores();
      }
      /* Check if stores exists */
      if(get().stores.length > 0) {
          /* Set active store */
          set({ activeStoreId: get().stores[0].id })
          /* Fetch transactions and expenses of store */
          await get().fetchGatewaysByStore();
          await get().fetchTransactions();
          await get().fetchExpenses();
          await get().fetchUsers();
      }
  
  },
  login: async (username, password) => {
    try {
        const res = await DatabaseService.signIn(username, password)
        set({
            token: res.session.access_token,
            userId: res.user.id
        })
        
        /* Fetch user details */
        const user = await DatabaseService.getUserById(res.user.id);
        set({ username: user.username, role: user.role })
        
        if(user.role == 'admin') {
            const allStores = await DatabaseService.getAllStores();
            set({ stores: allStores })
        } else {
            /* Fetch stores by user Id */
            const userStores = await DatabaseService.getStoresByUserId(res.user.id);
            set({ stores: userStores })
        }
        /* Check if stores exists */
        if(get().stores.length > 0) {
            /* Set active store */
            set({ activeStoreId: get().stores[0].id })
            /* Fetch transactions and expenses of store */
            await get().fetchGatewaysByStore();
            await get().fetchTransactions();
            await get().fetchExpenses();
            await get().fetchUsers();
        }

        return { ok: true, reason: "res.reason" }
    } catch (error) {
        return { ok: false, reason: "error.message" }
    }
  },
  logout: async () => {
    set({ token: null, userId: null, username: null, role: null })
    await DatabaseService.signOut();
  },
  /* User management */
  getCurrentUser: () => {
    const { userId, username, role } = get()
    return { userId, username, role }
  },

  fetchUsers: async () => {
    if(get().role !== 'admin') throw new Error("Only admin can fetch users");
    const users = await DatabaseService.getUsers();
    set({ users });
  },
  

  addUser: async (username, password, role, storeIds) => {
    if(get().role !== 'admin') throw new Error("Only admin can add users");
    await DatabaseService.addUser(username, password, role, storeIds);
    await get().fetchUsers();
  },

  updateUser: async (id, patch) => {
    if(get().role !== 'admin') throw new Error("Only admin can update users");
    await DatabaseService.updateUser(id, patch);
    await get().fetchUsers();
  },

  changePassword: async (id, newPassword) => {
    if(get().role !== 'admin') throw new Error("Only admin can change passwords");
    await DatabaseService.changePassword(id, newPassword);
    await get().fetchUsers();
  },
  removeUser: async (id) => {
    if(get().role !== 'admin') throw new Error("Only admin can remove users");
    await DatabaseService.removeUser(id);
    await get().fetchUsers();
  },
  // === Getters ===
  
  getTransactionsByStore: (storeId) =>
    get().transactions.filter((t) => t.store_id === storeId),
  getExpensesByStore: (storeId) =>
    get().expenses.filter((e) => e.store_id === storeId),
  getClosuresByStore: (storeId) =>
    get().monthlyClosures.filter((c) => c.store_id === storeId),
  
  // === Mutators ===
  setAuth: (token, userId, username, role) =>
    set({ token, userId, username, role }),
  clearAuth: () => set({ token: null, userId: null, username: null, role: null }),
  
  /* get store by id */
  getStoreById: (id) => get().stores.find((s) => s.id === id),
  setStores: (stores) => set({ stores }),
  setActiveStore: async (id) => {
      if(id === get().activeStoreId) return;
    set({ activeStoreId: id });
    await get().fetchGatewaysByStore();
    await get().fetchTransactions();
    await get().fetchExpenses();
  },
  fetchStores: async () => {
    if (get().role === "admin") {
      const stores = await DatabaseService.getAllStores()
      set({ stores })
    } else {
      const userStores = await DatabaseService.getStoresByUserId(get().userId!)
      set({ stores: userStores })
    }
  },
  addStore: async (name: string, logoUrl?: string) => {
    const id = await DatabaseService.addStore(name, logoUrl)
    /* Fetch stores again */
    await get().fetchStores();
    return id
  },

  updateStore: async (id: string, name: string, logoUrl?: string) => {
    await DatabaseService.updateStore(id, { name, logo_url: logoUrl })
    /* Fetch stores again */
    await get().fetchStores();
  },

  removeStore: async (id: string) => {
    await DatabaseService.removeStore(id)
    /* Fetch stores again */
    await get().fetchStores();
  },

  getStoreBalance: () => {
    const transactions = get().transactions
    const expenses = get().expenses
    /* Deposited transactions till now */
    const deposited = transactions.filter((t) => t.deposited).reduce((s, t) => s + t.net_amount, 0)
    /* Paid expenses till now */
    const paidExpenses = expenses.filter((e) => e.paid).reduce((s, e) => s + e.amount, 0)
    
    return deposited - paidExpenses
  },

  /* Gateway management */
  fetchGatewaysByStore: async () => {
    const activeStoreId = get().activeStoreId;
    if (!activeStoreId) return [];
    const gateways = await DatabaseService.getGatewaysByStoreId(activeStoreId);
    set({ gateways: gateways });
    return gateways;
  },

  addGateway: async (name: string) => {
    const activeStoreId = get().activeStoreId;
    if (!activeStoreId) throw new Error("No active store selected");
    /* Check if name already exists in gateways */
    if (get().gateways.find((g) => g.name === name)) return;
    /* Add gateway */
    await DatabaseService.addGateway(name, activeStoreId);
    await get().fetchGatewaysByStore();
  },

  removeGateway: async (id: string) => {
    await DatabaseService.removeGateway(id, get().activeStoreId!);
    await get().fetchGatewaysByStore();
  },
  /* Transaction management */
  setTransactions: (txs) => set({ transactions: txs }),
  fetchTransactions: async () => {
    const transactions = await DatabaseService.getTransactionsByStore(get().activeStoreId!);
    set({ transactions });
  },
  addTransaction: async (tx) => {
    await DatabaseService.addTransaction(
        {
            ...tx,
            net_amount: tx.amount_gross,
            deposited: (tx.deposit_date && new Date(tx.deposit_date) <= new Date()) ? true : false,
            store_id: get().activeStoreId!
        }
    );
    await get().fetchTransactions();
  },
  updateTransaction: async (id, patch) => {
    await DatabaseService.updateTransaction(id, patch);
    await get().fetchTransactions();
  },

  removeTransaction: async (id) => {
    await DatabaseService.deleteTransaction(id);
    await get().fetchTransactions();
  },

  /* Expense management */
  setExpenses: (exps) => set({ expenses: exps }),
  fetchExpenses: async () => {
    const expenses = await DatabaseService.getExpensesByStore(get().activeStoreId!);
    set({ expenses });
  },
  addExpense: async (exp) => {
    await DatabaseService.addExpense({
        ...exp,
        paid: (exp.payment_date && new Date(exp.payment_date) <= new Date()) ? true : false,
        store_id: get().activeStoreId!
    });
    await get().fetchExpenses();
  },
  updateExpense: async (id, patch) => {
    await DatabaseService.updateExpense(id, patch);
    await get().fetchExpenses();
  },

  removeExpense: async (id) => {
    await DatabaseService.deleteExpense(id);
    await get().fetchExpenses();
  },

      // Monthly helpers for active store
    computeMonthlySummary:  (month) => {


      const [yStr, mStr] = month.split('-')
      const start = new Date(Number(yStr), Number(mStr) - 1, 1, 0, 0, 0, 0)
      const end = new Date(Number(yStr), Number(mStr), 0, 23, 59, 59, 999)

      const txs = get().transactions
      const exs = get().expenses
      // Deposited by event date within month
      const salesDeposited = txs
        .filter((t) => t.deposited && t.deposit_date && inRange(new Date(t.deposit_date), start, end))
        .reduce((s, t) => s + t.net_amount, 0)
      const countSales = txs.filter((t) => t.deposited && t.deposit_date && inRange(new Date(t.deposit_date), start, end))
        .length

      // Pending deposits scheduled within month
      const pendingSales = txs
        .filter((t) => !t.deposited && inRange(new Date(t.deposit_date), start, end))
        .reduce((s, t) => s + t.net_amount, 0)

      const expensesPaid = exs
        .filter((e) => e.paid && e.payment_date && inRange(new Date(e.payment_date), start, end))
        .reduce((s, e) => s + e.amount, 0)
      const countExpenses = exs.filter((e) => e.paid && e.payment_date && inRange(new Date(e.payment_date), start, end)).length

      const pendingExpenses = exs
        .filter((e) => !e.paid && inRange(new Date(e.payment_date), start, end))
        .reduce((s, e) => s + e.amount, 0)

      const netProfit = salesDeposited - expensesPaid

      return {
        salesDeposited,
        expensesPaid,
        netProfit,
        countSales,
        countExpenses,
        pendingSales,
        pendingExpenses,
      }
    },
  setMonthlyClosures: (closures) => set({ monthlyClosures: closures }),
  addClosure: (closure) =>
    set((state) => ({
      monthlyClosures: [...state.monthlyClosures, closure],
    })),
  removeClosure: (month, storeId) =>
    set((state) => ({
      monthlyClosures: state.monthlyClosures.filter(
        (c) => !(c.month === month && c.store_id === storeId)
      ),
    })),
}))